import {execute, executeAfterTransition} from '@util/transition';

const CLASS_NAME_SELECTOR = 'backdrop';
const CLASS_NAME_ANIMATE = 'fade';
const CLASS_NAME_SHOW = 'show';

interface BackdropOptions {
    isVisible?: boolean;
    isAnimated?: boolean;
}

class Backdrop {
    private _element: HTMLElement | null;
    private readonly _isVisible: boolean;
    private readonly _isAnimated: boolean;
    private _isAppended: boolean;

    constructor(opts: BackdropOptions) {
        this._element = null;
        this._isVisible = opts.isVisible ?? true;
        this._isAnimated = opts.isAnimated ?? true;
        this._isAppended = false;
    }

    show(callback) {
        if (!this._isVisible) {
            execute(callback);
            return;
        }

        this._append();

        const element = this.getElement();

        element.classList.add(CLASS_NAME_SHOW);

        this._emulateAnimation(() => {
            execute(callback);
        });
    }

    hide(callback) {
        if (!this._isVisible) {
            execute(callback);
            return;
        }

        this.getElement().classList.remove(CLASS_NAME_SHOW);

        this._emulateAnimation(() => {
            this.dispose();
            execute(callback);
        });
    }

    dispose() {
        if (!this._isAppended) {
            return;
        }

        this._element.remove();
        this._isAppended = false;
    }

    // Private
    private getElement() {
        if (!this._element) {
            const backdrop = document.createElement('div');
            backdrop.className = CLASS_NAME_SELECTOR;

            if (this._isAnimated) {
                backdrop.classList.add(CLASS_NAME_ANIMATE);
            }

            this._element = backdrop;
        }

        return this._element;
    }

    _append() {
        if (this._isAppended) {
            return;
        }

        const element = this.getElement();
        document.body.appendChild(element);

        this._isAppended = true;
    }

    _emulateAnimation(callback) {
        executeAfterTransition(callback, this.getElement());
    }
}

export default Backdrop;
